import React, {useCallback, useEffect, useState} from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import {startAuthentication} from "@simplewebauthn/browser";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import {T, useTranslate} from "@tolgee/react";
import {AxiosError} from "axios";
import {apiV2} from "api/firebase";
import {isPasskeyCancellation} from "tools/auth/isPasskeyCancellation";
import {cancelSessionSwitch} from "tools/auth/sessionSync";
import {getErrorMessage, getRetryAfterSeconds} from "tools/error";
import {formatCountdown} from "tools/formats/formatCountdown";
import {maskEmail} from "tools/formats/maskEmail";
import {maskPhone} from "tools/formats/maskPhone";
import CodeInput from "view/components/CodeInput";
import Alert from "view/components/Alert";
import Copyright from "view/components/Copyright";
import KeyIcon from "view/images/KeyIcon";
import {
  renderTwoFactorMethodIcon,
  TWO_FACTOR_METHOD_LABEL_KEY,
} from "view/components/TwoFactorMethods/methods";
import FlexBox from "view/components/Layout/FlexBox";
import useStyles from "./style";

const EMAIL_RESEND_COOLDOWN_SECONDS = 30;

const SMS_DELIVERY_HINT_DELAY_MS = 20_000;

type Props = {
  pendingToken: string;
  method: TwoFactorMethodType;
  methods: TwoFactorMethodType[];
  onComplete: (customToken: string) => void | Promise<void>;
  onCancel?: () => void;
};

const TwoFactorChallenge: React.FC<Props> = ({
  pendingToken,
  method,
  methods,
  onComplete,
  onCancel,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();

  const [isVerifyingPasskey, setIsVerifyingPasskey] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [activeMethod, setActiveMethod] = useState<TwoFactorMethodType | null>(
    method
  );
  const [showMethodChooser, setShowMethodChooser] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState<string | null>(null);
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);
  const [showRecoveryCode, setShowRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryCodeError, setRecoveryCodeError] = useState<string | null>(
    null
  );
  const [isVerifyingRecoveryCode, setIsVerifyingRecoveryCode] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeError, setEmailCodeError] = useState<string | null>(null);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [hasSentEmailCode, setHasSentEmailCode] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState("");
  const [smsCodeError, setSmsCodeError] = useState<string | null>(null);
  const [isVerifyingSms, setIsVerifyingSms] = useState(false);
  const [isSendingSmsCode, setIsSendingSmsCode] = useState(false);
  const [hasSentSmsCode, setHasSentSmsCode] = useState(false);
  const [smsResendCooldown, setSmsResendCooldown] = useState(0);
  const [sentToPhone, setSentToPhone] = useState<string | null>(null);
  const [smsSendToken, setSmsSendToken] = useState(0);
  const [showSmsDeliveryHint, setShowSmsDeliveryHint] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const verifyTwoFactorCode = async (
    apiCall: () => Promise<{customToken: string}>,
    incorrectCodeMessage: string,
    expiredCodeMessage?: string
  ): Promise<string | null> => {
    try {
      const {customToken} = await apiCall();
      await onComplete(customToken);
      return null;
    } catch (e) {
      cancelSessionSwitch();
      const status = (e as AxiosError)?.response?.status;
      if (status === 429) return t("common.two_factor.too_many_attempts");
      const errorCode = (e as AxiosError<{message?: string}>)?.response?.data
        ?.message;
      if (expiredCodeMessage && errorCode?.endsWith("_CODE_EXPIRED")) {
        return expiredCodeMessage;
      }
      return incorrectCodeMessage;
    }
  };

  const handleVerifyPasskey = async () => {
    setPasskeyError(null);
    setIsVerifyingPasskey(true);
    try {
      const options = await apiV2.webauthn.getAuthenticationOptions({
        pendingToken,
      });
      const credential = await startAuthentication({optionsJSON: options});
      const {customToken} = await apiV2.webauthn.verifyAuthentication({
        pendingToken,
        credential,
        rememberDevice,
      });

      await onComplete(customToken);
    } catch (e) {
      cancelSessionSwitch();
      setPasskeyError(
        isPasskeyCancellation(e)
          ? t("global.login.passkey_cancelled")
          : t("global.login.passkey_verify_failed")
      );
    } finally {
      setIsVerifyingPasskey(false);
    }
  };

  const handleVerifyTotp = async (
    submittedCode: string = totpCode,
    silent = false
  ) => {
    setTotpError(null);
    setIsVerifyingTotp(true);
    const error = await verifyTwoFactorCode(
      () =>
        apiV2.totp.verifyAuthentication({
          pendingToken,
          code: submittedCode,
          rememberDevice,
        }),
      t("common.two_factor.incorrect_code")
    );
    if (error && !silent) setTotpError(error);
    setIsVerifyingTotp(false);
  };

  const selectMethod = (nextMethod: TwoFactorMethodType) => {
    setActiveMethod(nextMethod);
    setShowMethodChooser(false);
    setTotpCode("");
    setTotpError(null);
    setPasskeyError(null);
    setShowRecoveryCode(false);
    setEmailCode("");
    setEmailCodeError(null);
    setHasSentEmailCode(false);
    setEmailResendCooldown(0);
    setSentToEmail(null);
    setSmsCode("");
    setSmsCodeError(null);
    setHasSentSmsCode(false);
    setSmsResendCooldown(0);
    setSentToPhone(null);
  };

  const sendEmailCode = useCallback(async () => {
    setEmailCodeError(null);
    setIsSendingEmailCode(true);
    try {
      const {email} = await apiV2.emailTwoFactor.getAuthenticationOptions({
        pendingToken,
      });
      setSentToEmail(email);
      setHasSentEmailCode(true);
      setEmailResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      const status = (e as AxiosError)?.response?.status;
      setEmailCodeError(
        status === 429
          ? t("common.two_factor.too_many_attempts")
          : t("global.login.send_code_failed")
      );
    } finally {
      setIsSendingEmailCode(false);
    }
  }, [pendingToken, t]);

  useEffect(() => {
    if (emailResendCooldown <= 0) return undefined;

    const timeout = setTimeout(() => {
      setEmailResendCooldown((seconds) => seconds - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [emailResendCooldown]);

  useEffect(() => {
    if (activeMethod === "email") {
      sendEmailCode();
    }
  }, [activeMethod, sendEmailCode]);

  const handleVerifyEmail = async (
    submittedCode: string = emailCode,
    silent = false
  ) => {
    setEmailCodeError(null);
    setIsVerifyingEmail(true);
    const error = await verifyTwoFactorCode(
      () =>
        apiV2.emailTwoFactor.verifyAuthentication({
          pendingToken,
          code: submittedCode,
          rememberDevice,
        }),
      t("common.two_factor.incorrect_code"),
      t("common.two_factor.code_expired")
    );
    if (error && !silent) setEmailCodeError(error);
    setIsVerifyingEmail(false);
  };

  const sendSmsCode = useCallback(async () => {
    setSmsCodeError(null);
    setIsSendingSmsCode(true);
    try {
      const {
        phone,
        resendAfterSeconds,
      } = await apiV2.smsTwoFactor.getAuthenticationOptions({
        pendingToken,
      });
      setSentToPhone(phone);
      setHasSentSmsCode(true);
      // Driven by the server, not a constant: the wait escalates with each
      // unverified resend (60s → 5m → 30m → 24h), so a fixed 60 here re-enabled
      // the button while the API was still refusing.
      setSmsResendCooldown(resendAfterSeconds);
      setSmsSendToken((token) => token + 1);
    } catch (e) {
      // A rate-limit rejection reports how much of the window is left, so the
      // countdown stays honest even when this send never happened.
      const retryAfter = getRetryAfterSeconds(e);
      if (retryAfter) setSmsResendCooldown(retryAfter);

      const status = (e as AxiosError)?.response?.status;
      setSmsCodeError(
        status === 429 ? getErrorMessage(e) : t("global.login.send_code_failed")
      );
    } finally {
      setIsSendingSmsCode(false);
    }
  }, [pendingToken, t]);

  useEffect(() => {
    if (smsResendCooldown <= 0) return undefined;

    const timeout = setTimeout(() => {
      setSmsResendCooldown((seconds) => seconds - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [smsResendCooldown]);

  useEffect(() => {
    if (smsSendToken === 0) return undefined;

    setShowSmsDeliveryHint(false);
    const timeout = setTimeout(() => {
      setShowSmsDeliveryHint(true);
    }, SMS_DELIVERY_HINT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [smsSendToken]);

  useEffect(() => {
    if (activeMethod === "sms") {
      sendSmsCode();
    }
  }, [activeMethod, sendSmsCode]);

  const handleVerifySms = async (
    submittedCode: string = smsCode,
    silent = false
  ) => {
    setSmsCodeError(null);
    setIsVerifyingSms(true);
    const error = await verifyTwoFactorCode(
      () =>
        apiV2.smsTwoFactor.verifyAuthentication({
          pendingToken,
          code: submittedCode,
          rememberDevice,
        }),
      t("common.two_factor.incorrect_code"),
      t("common.two_factor.code_expired")
    );
    if (error && !silent) setSmsCodeError(error);
    setIsVerifyingSms(false);
  };

  const handleVerifyRecoveryCode = async () => {
    setRecoveryCodeError(null);
    setIsVerifyingRecoveryCode(true);
    const error = await verifyTwoFactorCode(
      () =>
        apiV2.recoveryCode.verifyAuthentication({
          pendingToken,
          code: recoveryCode,
          rememberDevice,
        }),
      t("global.login.invalid_code")
    );
    if (error) setRecoveryCodeError(error);
    setIsVerifyingRecoveryCode(false);
  };

  const otherMethods = methods.filter((item) => item !== activeMethod);

  const renderRememberDevice = () => (
    <Box width="100%">
      <FormControlLabel
        control={
          <Checkbox
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            color="primary"
          />
        }
        label={t("global.login.remember_device")}
        classes={{label: classes.rememberDeviceLabel}}
      />
    </Box>
  );

  const renderMethodBody = () => {
    if (showRecoveryCode) {
      return (
        <>
          <Box mt={2} mb={2} width="100%">
            <Typography variant="body2" className={classes.verifyText}>
              <T keyName="global.login.recovery_code_prompt" />
            </Typography>
          </Box>
          <TextField
            label={t("global.login.enter_recovery_code_label")}
            variant="outlined"
            fullWidth
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && recoveryCode) handleVerifyRecoveryCode();
            }}
            disabled={isVerifyingRecoveryCode}
            autoFocus
          />
          {recoveryCodeError && (
            <Box mt={2} width="100%">
              <Alert severity="error">{recoveryCodeError}</Alert>
            </Box>
          )}
          <Box mt={1}>{renderRememberDevice()}</Box>
          <Box mt={2} className={classes.continueRow}>
            <Button
              variant="contained"
              color="primary"
              className={classes.passkeyButton}
              disabled={isVerifyingRecoveryCode || !recoveryCode}
              endIcon={
                isVerifyingRecoveryCode ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              onClick={handleVerifyRecoveryCode}
            >
              <T keyName="common.action.continue" />
            </Button>
          </Box>
        </>
      );
    }

    if (showMethodChooser) {
      return (
        <Box mt={2} className={classes.methodList}>
          {methods.map((item) => (
            <ButtonBase
              key={item}
              className={classes.methodRow}
              onClick={() => selectMethod(item)}
            >
              <div className={classes.methodIconBox}>
                {renderTwoFactorMethodIcon(item)}
              </div>
              <div className={classes.methodContent}>
                <Typography className={classes.methodTitle}>
                  <T keyName={TWO_FACTOR_METHOD_LABEL_KEY[item]} />
                </Typography>
              </div>
              <ArrowForwardIcon className={classes.methodArrow} />
            </ButtonBase>
          ))}

          <FlexBox fullWidth justifyCenter>
            <Button
              color="primary"
              variant="text"
              onClick={() => {
                setShowMethodChooser(false);
                setShowRecoveryCode(true);
              }}
              className={classes.recoveryBtn}
            >
              <T keyName="global.login.use_recovery_code" />
            </Button>
          </FlexBox>
        </Box>
      );
    }

    if (activeMethod === "totp") {
      return (
        <>
          <Box mt={2} mb={2} width="100%">
            <Typography variant="body2" className={classes.verifyText}>
              <T keyName="global.login.authenticator_prompt" />
            </Typography>
          </Box>
          <CodeInput
            value={totpCode}
            onChange={setTotpCode}
            disabled={isVerifyingTotp}
            autoFocus
            onComplete={handleVerifyTotp}
          />
          {totpError && (
            <Box mt={2} width="100%">
              <Alert severity="error">{totpError}</Alert>
            </Box>
          )}
          <Box mt={1}>{renderRememberDevice()}</Box>
          <Box mt={2} className={classes.continueRow}>
            <Button
              variant="contained"
              color="primary"
              className={classes.passkeyButton}
              disabled={isVerifyingTotp || totpCode.length < 6}
              endIcon={
                isVerifyingTotp ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              onClick={() => handleVerifyTotp()}
            >
              <T keyName="common.action.continue" />
            </Button>
          </Box>
        </>
      );
    }

    if (activeMethod === "passkey") {
      return (
        <>
          <Box mt={2} mb={2} width="100%">
            <Typography variant="body2" className={classes.verifyText}>
              <T keyName="global.login.passkey_prompt" />
            </Typography>
          </Box>
          {passkeyError && (
            <Box mb={2} width="100%">
              <Alert severity="error">{passkeyError}</Alert>
            </Box>
          )}
          <Box mb={1}>{renderRememberDevice()}</Box>
          <Button
            variant="contained"
            color="primary"
            className={classes.passkeyButton}
            disabled={isVerifyingPasskey}
            endIcon={
              isVerifyingPasskey ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={handleVerifyPasskey}
          >
            <T keyName="global.login.continue_with_passkey" />
          </Button>
        </>
      );
    }

    if (activeMethod === "email") {
      return (
        <>
          <Box mt={2} mb={2} width="100%">
            <Typography variant="body2" className={classes.verifyText}>
              {hasSentEmailCode ? (
                <T
                  keyName="global.login.email_code_prompt"
                  params={{
                    email: sentToEmail
                      ? maskEmail(sentToEmail)
                      : t("global.login.your_email"),
                  }}
                />
              ) : isSendingEmailCode ? (
                "Sending a 6-digit code to your email."
              ) : (
                <T keyName="global.login.email_send_hint" />
              )}
            </Typography>
          </Box>
          {hasSentEmailCode && (
            <CodeInput
              value={emailCode}
              onChange={setEmailCode}
              disabled={isVerifyingEmail}
              autoFocus
              onComplete={handleVerifyEmail}
            />
          )}
          {emailCodeError && (
            <Box mt={2} width="100%">
              <Alert severity="error">{emailCodeError}</Alert>
            </Box>
          )}
          {(hasSentEmailCode || emailCodeError) && (
            <Box mt={1}>
              <Button
                color="primary"
                disabled={isSendingEmailCode || emailResendCooldown > 0}
                endIcon={
                  isSendingEmailCode ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
                onClick={sendEmailCode}
              >
                {emailResendCooldown > 0
                  ? `Resend Email (0:${String(emailResendCooldown).padStart(
                      2,
                      "0"
                    )})`
                  : "Resend Email"}
              </Button>
            </Box>
          )}
          {hasSentEmailCode && (
            <>
              <Box mt={1}>{renderRememberDevice()}</Box>
              <Box mt={2} className={classes.continueRow}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.passkeyButton}
                  disabled={isVerifyingEmail || emailCode.length < 6}
                  endIcon={
                    isVerifyingEmail ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                  onClick={() => handleVerifyEmail()}
                >
                  <T keyName="common.action.continue" />
                </Button>
              </Box>
            </>
          )}
        </>
      );
    }

    if (activeMethod === "sms") {
      return (
        <>
          <Box mt={2} mb={2} width="100%">
            <Typography variant="body2" className={classes.verifyText}>
              {hasSentSmsCode ? (
                <T
                  keyName="global.login.sms_code_prompt"
                  params={{
                    phone: sentToPhone
                      ? maskPhone(sentToPhone)
                      : t("global.login.your_phone"),
                  }}
                />
              ) : isSendingSmsCode ? (
                "Sending a 6-digit code to your phone."
              ) : (
                <T keyName="global.login.sms_send_hint" />
              )}
            </Typography>
          </Box>
          {hasSentSmsCode && (
            <CodeInput
              value={smsCode}
              onChange={setSmsCode}
              disabled={isVerifyingSms}
              autoFocus
              onComplete={handleVerifySms}
            />
          )}
          {smsCodeError && (
            <Box mt={2} width="100%">
              <Alert severity="error">{smsCodeError}</Alert>
            </Box>
          )}
          {showSmsDeliveryHint && !smsCodeError && (
            <Box mt={2} width="100%">
              <Alert severity="info">
                <T keyName="global.login.sms_delivery_hint" />
              </Alert>
            </Box>
          )}
          {(hasSentSmsCode || smsCodeError) && (
            <Box mt={1}>
              <Button
                color="primary"
                disabled={isSendingSmsCode || smsResendCooldown > 0}
                endIcon={
                  isSendingSmsCode ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
                onClick={sendSmsCode}
              >
                {smsResendCooldown > 0
                  ? `Resend SMS (${formatCountdown(smsResendCooldown)})`
                  : "Resend SMS"}
              </Button>
            </Box>
          )}
          {hasSentSmsCode && (
            <>
              <Box mt={1}>{renderRememberDevice()}</Box>
              <Box mt={2} className={classes.continueRow}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.passkeyButton}
                  disabled={isVerifyingSms || smsCode.length < 6}
                  endIcon={
                    isVerifyingSms ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                  onClick={() => handleVerifySms()}
                >
                  <T keyName="common.action.continue" />
                </Button>
              </Box>
            </>
          )}
        </>
      );
    }

    return (
      <Box mt={2} mb={2} width="100%">
        <Typography variant="body2" className={classes.verifyText}>
          <T keyName="global.login.method_unavailable" />
        </Typography>
      </Box>
    );
  };

  return (
    <Box className={classes.content}>
      <Box className={classes.container}>
        <Paper
          elevation={0}
          className={`${classes.paper} ${classes.secondFactorPaper}`}
        >
          <CssBaseline />
          <KeyIcon />
          <Typography className={classes.signInText}>
            {showRecoveryCode
              ? t("global.login.enter_recovery_code")
              : showMethodChooser
              ? t("global.login.try_another_method_long")
              : t("global.login.verify_device")}
          </Typography>
          {renderMethodBody()}
          {!showMethodChooser && !showRecoveryCode && (
            <Box mt={2} className={classes.secondFactorLinksRow}>
              <Button
                className={classes.secondFactorLink}
                disabled={
                  isVerifyingTotp ||
                  isVerifyingPasskey ||
                  isVerifyingEmail ||
                  isVerifyingSms
                }
                onClick={() => setShowRecoveryCode(true)}
              >
                <T keyName="global.login.use_recovery_code" />
              </Button>
              {otherMethods.length > 0 && (
                <>
                  <span className={classes.secondFactorLinkDot}>•</span>
                  <Button
                    className={classes.secondFactorLink}
                    disabled={
                      isVerifyingTotp ||
                      isVerifyingPasskey ||
                      isVerifyingEmail ||
                      isVerifyingSms
                    }
                    onClick={() => setShowMethodChooser(true)}
                  >
                    <T keyName="global.login.try_another_method" />
                  </Button>
                </>
              )}
            </Box>
          )}
          {onCancel && (
            <Box mt={2} className={classes.continueRow}>
              <Button
                className={classes.backToLoginLink}
                disabled={
                  isVerifyingTotp ||
                  isVerifyingPasskey ||
                  isVerifyingRecoveryCode ||
                  isVerifyingEmail ||
                  isVerifyingSms
                }
                onClick={onCancel}
              >
                <T keyName="global.login.back_to_login" />
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      <Box>
        <Copyright />
      </Box>
    </Box>
  );
};

export default TwoFactorChallenge;
